/**
 * FUER WEN wird gebucht — die Auswahl des Ausfuehrenden im Buchungsdialog.
 *
 * Bisher stand bei jeder manuellen Buchung im Projekt der angemeldete Nutzer
 * als `performerId`/`performerName`. Die Buchhaltung traegt aber regelmaessig
 * eine Begehung FUER einen Koordinator nach — und dann stand im Protokoll
 * und im Leistungsnachweis der Name der Sachbearbeiterin, nicht der des
 * Koordinators, der draussen war.
 *
 * Deshalb: wer das Recht „Leistung für andere buchen" hat (Admins immer),
 * bekommt im Dialog ein Feld „Erbracht durch". Vorbelegt mit dem
 * Projektkoordinator, denn fuer den wird in der Regel nachgetragen; der
 * Buchende selbst steht ebenfalls in der Liste. Wer das Recht nicht hat,
 * sieht das Feld nicht und bucht wie bisher auf sich selbst.
 *
 * Reine Ableitung, kein Schreibpfad.
 */
import { UserProfile } from '../types';
import { buildActorName } from './actorName';
import type { RechteSicht } from './rechte/rechteService';

export interface PerformerOption {
    id: string;
    name: string;
    /** Rolle im Projekt — nur fuer die Anzeige hinter dem Namen. */
    hint?: 'Koordinator' | 'Vertretung' | 'Sie';
}

export interface PerformerChoice {
    performerId: string;
    performerName: string;
}

/**
 * Fuer jemand anderen buchen darf, wer das Recht `leistung.fuer_andere_buchen`
 * hat — Admin und Super-Admin immer; ohne geladene Rechte gilt das heutige
 * Verhalten (nur Admins). Nur Oberflaeche: die Datenbank prueft den
 * Ausfuehrenden nicht (docs/rollensystem-plan.md §11).
 */
export const canChoosePerformer = (rechte: Pick<RechteSicht, 'hat'>): boolean =>
    rechte.hat('leistung.fuer_andere_buchen');

interface ProjectRoleIds {
    coordinatorId?: string;
    coordinatorDeputyId?: string;
    coordinatorDeputy2Id?: string;
}

/**
 * Auswahlliste: Koordinator zuerst, dann Vertretungen, dann alle uebrigen
 * Kollegen alphabetisch. Der angemeldete Nutzer ist immer dabei — auch wenn
 * er (noch) nicht in `availableUsers` steht. Freelancer sind ausgenommen:
 * ihre Leistungen laufen ueber das Freelancer-Portal, nicht ueber diesen
 * Dialog. Nutzer ohne verwertbaren Namen (actorName) fallen heraus, weil sie
 * sonst als leere Zeile im Nachweis stuenden. Gesperrte Mitglieder (Nr. 148,
 * `gesperrt`) stehen nicht zur Wahl — auch nicht als Projektkoordinator.
 */
export const buildPerformerOptions = (
    availableUsers: UserProfile[],
    currentUser: UserProfile | null | undefined,
    project: ProjectRoleIds,
    gesperrt: ReadonlySet<string> = new Set(),
): PerformerOption[] => {
    const byId = new Map<string, UserProfile>();
    for (const u of availableUsers) {
        if (!u?.id || u.role === 'freelancer' || u.is_approved === false || gesperrt.has(u.id)) continue;
        byId.set(u.id, u);
    }
    if (currentUser?.id && !byId.has(currentUser.id)) byId.set(currentUser.id, currentUser);

    const named = (u: UserProfile): PerformerOption | null => {
        const name = buildActorName(u);
        return name ? { id: u.id, name } : null;
    };

    const result: PerformerOption[] = [];
    const taken = new Set<string>();
    const push = (id: string | undefined, hint: PerformerOption['hint']) => {
        if (!id || taken.has(id)) return;
        const u = byId.get(id);
        if (!u) return;
        const opt = named(u);
        if (!opt) return;
        taken.add(id);
        result.push({ ...opt, hint });
    };
    push(project.coordinatorId, 'Koordinator');
    push(project.coordinatorDeputyId, 'Vertretung');
    push(project.coordinatorDeputy2Id, 'Vertretung');

    const rest = [...byId.values()]
        .filter(u => !taken.has(u.id))
        .map(named)
        .filter((o): o is PerformerOption => o !== null)
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    for (const o of rest) {
        result.push(o.id === currentUser?.id ? { ...o, hint: 'Sie' } : o);
    }
    return result;
};

/**
 * Vorbelegung: der Projektkoordinator, wenn er in der Liste steht — sonst der
 * angemeldete Nutzer, sonst der erste Eintrag.
 */
export const defaultPerformerId = (
    options: PerformerOption[],
    currentUser: UserProfile | null | undefined,
    project: ProjectRoleIds,
): string => {
    const has = (id?: string) => !!id && options.some(o => o.id === id);
    if (has(project.coordinatorId)) return project.coordinatorId as string;
    if (has(currentUser?.id)) return currentUser?.id as string;
    return options[0]?.id ?? '';
};

/**
 * Aus der Auswahl wird das Paar, das in den Protokolleintrag geht. Ohne
 * Auswahl (Nicht-Admin, leere Liste, unbekannte id) bleibt es beim bisherigen
 * Verhalten: der angemeldete Nutzer, mit '' als Name, wenn das Profil keinen
 * verwertbaren traegt (dann greift im Nachweis der Archivname).
 */
export const resolvePerformer = (
    chosenId: string | undefined,
    options: PerformerOption[],
    currentUser: UserProfile | null | undefined,
): PerformerChoice => {
    const chosen = chosenId ? options.find(o => o.id === chosenId) : undefined;
    if (chosen) return { performerId: chosen.id, performerName: chosen.name };
    return { performerId: currentUser?.id || 'system', performerName: buildActorName(currentUser) };
};
